import axios from 'axios';
import { getAuthTokenFromStorage, AUTH_TOKEN_KEY } from '../utils/authStorage';
import { API_BASE_URL } from './apiConfig';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export { AUTH_TOKEN_KEY };

function getAuthHeaders() {
  const token = getAuthTokenFromStorage();
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

api.interceptors.request.use((config) => {
  const auth = getAuthHeaders();
  if (auth.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = auth.Authorization;
  }
  return config;
});

export { api };

export const analyzeArticle = async (articleData) => {
  try {
    const response = await api.post('/analyze', articleData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to analyze article');
  }
};

export const analyzeBatch = async (articlesData) => {
  try {
    const response = await api.post('/analyze/batch', articlesData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to analyze batch');
  }
};

export const getArticles = async (params = {}) => {
  try {
    const response = await api.get('/articles', { params });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch articles');
  }
};

export const getSummary = async () => {
  try {
    const response = await api.get('/summary');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch summary');
  }
};

/** CAS (Central Alert System) executive summary: price, trade, reserve, and news alerts with recommendations */
export const getCasExecutiveSummary = async (limit = 100) => {
  try {
    const response = await api.get('/api/cas/executive-summary', { params: { limit } });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch CAS executive summary');
  }
};

/** Get a single Claude-consolidated Executive Summary (Price, Trade, Reserve, News) for a mineral. */
export const getCasConsolidatedSummary = async (mineral = 'All') => {
  try {
    const response = await api.get('/api/cas/consolidated-summary', { params: { mineral } });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch consolidated summary');
  }
};

/** Start syncing news from analyzed_articles into CAS (AI enrichment). Runs in background. */
export const syncCasNews = async () => {
  try {
    const response = await api.post('/api/cas/sync-news');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to start CAS news sync');
  }
};

export const getTrendingKeywords = async (days = 30, limit = 20) => {
  try {
    const response = await api.get('/trending', { params: { days, limit } });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch trending keywords');
  }
};

export const getDomesticReserves = async () => {
  try {
    const response = await api.get('/api/domestic-reserves');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch domestic reserves data');
  }
};

export const getDomesticReservesByState = async (state) => {
  try {
    const response = await api.get(`/api/domestic-reserves/by-state/${state}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || `Failed to fetch domestic reserves for ${state}`);
  }
};

export const searchArticles = async (query, limit = 50) => {
  try {
    const response = await api.get('/search', { params: { query, limit } });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to search articles');
  }
};

export const getHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    throw new Error('API server is not responding');
  }
};

// Strategic Exposure API endpoints
export const getStrategicExposureData = async () => {
  try {
    const response = await api.get('/api/strategic-exposure');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch strategic exposure data');
  }
};

export const getMineralDetail = async (mineralName) => {
  try {
    const response = await api.get(`/api/strategic-exposure/mineral/${encodeURIComponent(mineralName)}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch mineral detail');
  }
};

export const getCountryDetail = async (countryName) => {
  try {
    const response = await api.get(`/api/strategic-exposure/country/${encodeURIComponent(countryName)}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch country detail');
  }
};

// Foresight API endpoints
export const getForesightData = async () => {
  try {
    const response = await api.get('/api/foresight');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch foresight data');
  }
};

// Policy & Market Intelligence API endpoints
export const getPolicyMarketData = async () => {
  try {
    const response = await api.get('/api/policy-market-intelligence');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch policy market data');
  }
};

// Market Intelligence API endpoints
export const getMarketIntelligenceData = async () => {
  try {
    const response = await api.get('/api/market-intelligence');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch market intelligence data');
  }
};

export const getMineralMarketDetail = async (mineralName) => {
  try {
    const response = await api.get(`/api/market-intelligence/mineral/${encodeURIComponent(mineralName)}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch mineral market detail');
  }
};

// Export API endpoints
export const exportData = async (format, dataType = 'analyzed', includeRawData = false) => {
  try {
    const response = await api.post('/export', {
      format: format.toLowerCase(),
      data_type: dataType,
      include_raw_data: includeRawData
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to export data');
  }
};

export const downloadExportedFile = async (filePath) => {
  try {
    const response = await api.get(`/download/${encodeURIComponent(filePath)}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filePath.split('/').pop());
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    throw new Error('Failed to download exported file');
  }
};

// --- Auth: set token for requests (call after login) ---
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Login API endpoint
export const login = async (username, password) => {
  try {
    const response = await api.post('/api/login', {
      username,
      password
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Register API endpoint
export const register = async (data) => {
  try {
    const response = await api.post('/api/register', {
      username: data.username,
      email: data.email,
      full_name: data.full_name,
      password: data.password,
      registration_type: data.registration_type,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Logout: invalidate session token on server (optional)
export const logoutApi = async (token) => {
  try {
    if (token) {
      await api.post('/api/logout', null, { headers: { Authorization: `Bearer ${token}` } });
    }
  } catch (_) {}
};

// Get current user (requires token)
export const getMe = async () => {
  try {
    const response = await api.get('/api/me', { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post(
    '/api/me/change-password',
    { current_password: currentPassword, new_password: newPassword },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const updateMyProfile = async (payload) => {
  const body = {};
  if (payload.full_name != null) body.full_name = payload.full_name;
  if (payload.email != null) body.email = payload.email;
  const response = await api.patch('/api/me/profile', body, { headers: getAuthHeaders() });
  return response.data;
};

// Subscription plans (for registration form)
export const getSubscriptionPlans = async () => {
  try {
    const response = await api.get('/api/subscription-plans');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// SuperAdmin: list all subscription plans (including features)
export const getAdminSubscriptionPlans = async () => {
  try {
    const response = await api.get('/api/admin/subscription-plans', { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// SuperAdmin: registration type page access (Corporate / Policy Maker)
export const getRegistrationTypePageAccess = async () => {
  try {
    const response = await api.get('/api/admin/registration-types', {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateRegistrationTypePageAccess = async (registrationType, features) => {
  try {
    const response = await api.patch(
      `/api/admin/registration-types/${registrationType}/features`,
      { features },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// SuperAdmin: update plan features
export const updateSubscriptionPlanFeatures = async (planId, features) => {
  try {
    const response = await api.patch(
      `/api/admin/subscription-plans/${planId}/features`,
      { features },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// SuperAdmin: list users (Bearer token from storage via interceptor + getAuthHeaders)
export const getUsers = async () => {
  try {
    const response = await api.get('/api/users', { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get user by id
export const getUser = async (userId) => {
  try {
    const response = await api.get(`/api/users/${userId}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// SuperAdmin: update user
export const updateUser = async (userId, data) => {
  try {
    const response = await api.patch(`/api/users/${userId}`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api; 